var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-native-dialog.ts
import { logger } from "@adornis/base/logging.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import { XSnackbar } from "@adornis/chemistry/elements/components/x-snackbar.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { routingState } from "@adornis/router/client/routing-state.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { distinctUntilChanged, Observable, skip, Subject, takeUntil } from "rxjs";
import { DialogCancledDueToRouteChange } from "./error.js";
var defaultOptions = {
  props: {},
  modal: false,
  closeOnNavigate: true,
  closeOnEscape: true
};
var XNativeDialog = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.animationBehavior = "zoom";
    this._opened = false;
    this.result = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
    this._dialog = createRef();
  }
  // SECTION API
  // toggle() {
  //   if (!this._opened) return this.open();
  //   return this.close();
  // }
  open() {
    this._opened = true;
    return this.result;
  }
  close(returnVal) {
    this._opened = false;
    this.resolve(returnVal);
    if (Array.from(document.body.childNodes.values()).some((node) => node === this))
      document.body.removeChild(this);
  }
  cancel(returnVal) {
    this._opened = false;
    this.reject(returnVal);
    if (Array.from(document.body.childNodes.values()).some((node) => node === this))
      document.body.removeChild(this);
  }
  get isOpen() {
    return this._opened;
  }
  getAnimationBehavior() {
    let behavior = this.constructor.animationBehaviors[this.animationBehavior];
    if (!behavior)
      behavior = this.constructor.animationBehaviors.zoom;
    return behavior;
  }
  styles() {
    const behavior = this.getAnimationBehavior();
    return [
      ...super.styles(),
      {
        "@keyframes backdrop-fade": {
          "0%": behavior.from.backdrop,
          "100%": behavior.to.backdrop
        },
        "@keyframes dialog-fade": {
          "0%": behavior.from.dialog,
          "100%": behavior.to.dialog
        },
        dialog: {
          outline: "none",
          boxShadow: `0 0 30px ${this.colors.neutral.shade(500)}`,
          padding: 0,
          border: "none",
          margin: "auto",
          transition: behavior.transition.dialog.map((transitionKey) => `${transitionKey} ${behavior.animationDuration / 1e3}s ease-out`).join(", "),
          animation: `dialog-fade ${behavior.animationDuration / 1e3}s ease-out`,
          ...behavior.from.dialog,
          "&::backdrop": {
            transition: behavior.transition.backdrop.map((transitionKey) => `${transitionKey} ${behavior.animationDuration / 1e3}s ease-out`).join(", "),
            animation: `backdrop-fade ${behavior.animationDuration / 1e3}s ease-out`,
            ...behavior.from.backdrop
          },
          "&[open]:not([is-closing])": {
            ...behavior.to.dialog,
            "&::backdrop": behavior.to.backdrop
          }
        }
      }
    ];
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.options?.closeOnNavigate)
      routingState.path$.pipe(distinctUntilChanged(), skip(1), takeUntil(this.disconnected)).subscribe(() => this.cancel(DialogCancledDueToRouteChange));
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("_opened")) {
      if (this._opened) {
        this._dialog.value?.showModal();
      } else {
        setTimeout(() => this._dialog.value?.close(), this.getAnimationBehavior().animationDuration);
      }
      if (changedProperties.get("_opened") && !this._opened) {
        this.dispatchEvent(new CustomEvent("close"));
      }
    }
  }
  render() {
    return html`
      <dialog
        ?is-closing=${!this._opened}
        ${css(this._dialogStyle ?? {})}
        ${ref(this._dialog)}
        @click=${(e) => {
      if (e.target === this._dialog.value && !this.options?.modal)
        this.cancel();
    }}
        @close=${() => {
      if (this._opened)
        this.cancel();
    }}
      >
        ${this.content()}
      </dialog>
    `;
  }
  content() {
    return html`
      <x-flex> ${this.dialogShowPopupContent ? this.dialogShowPopupContent : html` <slot></slot> `} </x-flex>
    `;
  }
  get _dialogStyle() {
    return null;
  }
  // SECTION STATIC DIALOG USAGE
  static get element_name() {
    if (this === XNativeDialog)
      return "x-native-dialog";
    throw new Error(
      "You need to define the element_name getter in order to make x-native-dialog (static usage) work properly: " + this.name
    );
  }
  initDialog(options = defaultOptions) {
    this.options = { ...defaultOptions, ...options };
    Object.entries(this.options.props).forEach(([key, value]) => {
      const self = this;
      if (self[key] instanceof Subject)
        self[key].next(value);
      else
        self[key] = value;
    });
  }
  static create(options) {
    const dialog = document.createElement(this.element_name);
    dialog.initDialog(options);
    document.body.appendChild(dialog);
    return dialog;
  }
  static showPopup(options = {}) {
    const dialog = this.create(options);
    if (options.content) {
      const closeAndResolve = (value) => dialog.close(value);
      const closeAndReject = (value) => dialog.cancel(value);
      const contentToUse = typeof options.content === "function" ? options.content(closeAndResolve, closeAndReject, dialog) : options.content;
      dialog.dialogShowPopupContent = contentToUse;
    }
    return dialog.open();
  }
  static confirm(text, {
    highlightDecline = false,
    acceptText = "Ja",
    declineText = "Nein"
  } = {}, options) {
    return this.showPopup({
      ...options,
      modal: true,
      closeOnEscape: true,
      content: (r) => {
        const enterListener = (e) => {
          if (e.key === "Enter")
            resolve(true);
        };
        document.addEventListener("keydown", enterListener);
        const resolve = (v) => {
          document.removeEventListener("keydown", enterListener);
          r(v);
        };
        return xComponents(html`
          <x-flex space="md" padding="md">
            <x-text>${text}</x-text>
            <x-flex horizontal end space="md">
              <x-button mode="${highlightDecline ? "action" : "outline"}" @click="${() => resolve(false)}">
                <x-text> ${declineText} </x-text>
              </x-button>
              <x-button mode="${highlightDecline ? "outline" : "action"}" @click="${() => resolve(true)}">
                <x-text> ${acceptText} </x-text>
              </x-button>
            </x-flex>
          </x-flex>
        `);
      }
    }).catch(() => false);
  }
  static alert(text, buttonText = "Ok", options) {
    return this.showPopup({
      modal: true,
      ...options,
      closeOnEscape: true,
      content: (r) => {
        const enterListener = (e) => {
          if (e.key === "Enter")
            resolve();
        };
        document.addEventListener("keydown", enterListener);
        const resolve = () => {
          document.removeEventListener("keydown", enterListener);
          r();
        };
        return xComponents(html`
          <x-flex space="md" padding="md">
            ${typeof text === "string" ? html`<x-text> ${text} </x-text>` : text}
            <x-flex horizontal end space="md">
              <x-button @click="${() => resolve()}"> ${buttonText} </x-button>
            </x-flex>
          </x-flex>
        `);
      }
    }).catch(() => {
    });
  }
  static prompt(text, { placeholder = "", buttonText = "Ok" } = {}, options) {
    return this.showPopup({
      ...options,
      closeOnEscape: true,
      content: (r, reject) => {
        let response = "";
        const enterListener = (e) => {
          if (e.key === "Enter")
            resolve(response);
        };
        document.addEventListener("keydown", enterListener);
        const resolve = (v) => {
          document.removeEventListener("keydown", enterListener);
          r(v);
        };
        return xComponents(html`
          <x-flex space="md" padding="md">
            <x-text>${text}</x-text>
            <x-input
              ${ref((e) => requestAnimationFrame(() => e?.focus()))}
              placeholder-mode="hide"
              placeholder=${placeholder}
              @value-changed=${(e) => response = e.detail.value}
            ></x-input>
            <x-flex horizontal end space="md">
              <x-button @click=${() => resolve(response)}> <x-text> ${buttonText} </x-text> </x-button>
            </x-flex>
          </x-flex>
        `);
      }
    });
  }
  static waitFor(promiseOrObservable, text, options = {}) {
    if (promiseOrObservable instanceof Observable) {
      console.log("open observable");
      return this.showPopup({
        ...options,
        modal: true,
        content: (resolve, reject, dialog) => {
          let lastStr = "";
          promiseOrObservable.pipe(takeUntil(dialog.disconnected)).subscribe({
            next: (str) => {
              lastStr = str;
              const wrapper = dialog.renderRoot.querySelector("#text-wrapper");
              if (!wrapper)
                return;
              return wrapper.innerHTML = ` ${text || str ? ` <x-text> ${str ?? text} </x-text> ` : ""} `;
            },
            complete: () => resolve(lastStr)
          });
          return xComponents(html`
            <x-flex padding="xxl" space="lg" crossaxis-center center id="loader-wrapper">
              <x-loader></x-loader>
              <div id="text-wrapper">${text ? html` <x-text> ${text} </x-text> ` : nothing}</div>
            </x-flex>
          `);
        }
      });
    }
    return this.showPopup({
      ...options,
      modal: true,
      content: (resolve, reject, dialog) => {
        promiseOrObservable.then((val) => resolve(val)).catch((error) => reject(error));
        return xComponents(html`
          <x-flex padding="xxl" space="lg" crossaxis-center center>
            <x-loader></x-loader>
            ${text ? html` <x-text> ${text} </x-text> ` : nothing}
          </x-flex>
        `);
      }
    });
  }
  static wait(text, options = {}) {
    let resolveReturn;
    void this.showPopup({
      ...options,
      modal: true,
      content: (resolve, reject, dialog) => {
        resolveReturn = resolve;
        return xComponents(html`
          <x-flex padding="xxl" space="lg" crossaxis-center center>
            <x-loader></x-loader>
            ${text ? html` <x-text> ${text} </x-text> ` : nothing}
          </x-flex>
        `);
      }
    });
    return resolveReturn;
  }
  static success(text, buttonText = "Ok", options) {
    return this.showPopup({
      ...options,
      content: (resolve) => {
        return xComponents(html`
          <x-flex space="md" padding="md">
            <x-text>${text}</x-text>
            <x-flex horizontal end space="md">
              <x-button @click=${() => resolve()}> <x-text> ${buttonText} </x-text> </x-button>
            </x-flex>
          </x-flex>
        `);
      }
    });
  }
  static async guard(task, options, dialogOptions) {
    let result;
    try {
      if (options?.hasToConfirm && !await this.confirm(options.confirmText ?? "Wirklich?"))
        return;
      if (options?.showLoaderModal === false) {
        result = await task();
      } else {
        void this.waitFor(new Promise((resolve) => setTimeout(() => resolve(), 200)), void 0, dialogOptions);
        result = await this.waitFor(task(), void 0, dialogOptions);
      }
      if (options?.success)
        await options.success(result);
      else
        void XSnackbar.show("Das hat geklappt");
      return result;
    } catch (e) {
      logger.error(e);
      if (!e?.message)
        return;
      const m = e.message;
      if (options?.error)
        return await options.error(e);
      else
        return this.alert(
          html`
            <x-text>
              ${options?.errorMessagePrefix ?? "Fehler beim Speichern"}: ${m.includes("\n") ? "\n" : ""}
              ${m.split("\n").reduce((template, curr) => html`${template}<br />${curr}`, html``)}
            </x-text>
          `
        );
    }
  }
};
// ENDSECTION API
XNativeDialog.animationBehaviors = {
  zoom: {
    from: { dialog: { opacity: "0", transform: "scale(0.9)" }, backdrop: { backgroundColor: "rgba(0,0,0,0)" } },
    to: { dialog: { opacity: "1", transform: "scale(1)" }, backdrop: { backgroundColor: "rgba(0,0,0,0.1)" } },
    transition: { dialog: ["transform", "opacity"], backdrop: ["background-color"] },
    animationDuration: 150
  },
  slideRight: {
    from: { dialog: { opacity: "0", transform: "translateX(100%)" }, backdrop: { backgroundColor: "rgba(0,0,0,0)" } },
    to: { dialog: { opacity: "1", transform: "translateX(0)" }, backdrop: { backgroundColor: "rgba(0,0,0,0.1)" } },
    transition: { dialog: ["transform", "opacity"], backdrop: ["background-color"] },
    animationDuration: 150
  }
};
__decorateClass([
  property({ attribute: "animation-behavior" })
], XNativeDialog.prototype, "animationBehavior", 2);
__decorateClass([
  state()
], XNativeDialog.prototype, "_opened", 2);
XNativeDialog = __decorateClass([
  customElement("x-native-dialog")
], XNativeDialog);
export {
  XNativeDialog
};
